import React, { useState, useEffect } from 'react';
import { FiSave, FiRotateCcw, FiGlobe, FiClock, FiDatabase } from 'react-icons/fi';
import adminSettingsService from '../../../services/adminSettingsService';
import { Loading } from '../../../components/UI/Loading';

const SystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const result = await adminSettingsService.getSystemSettings();
      if (result.success) {
        setSettings(result.data);
        setOriginalSettings(result.data);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al cargar configuraciones' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const result = await adminSettingsService.updateSystemSettings(settings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Configuración actualizada correctamente' });
        setOriginalSettings(settings);
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Error al actualizar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('¿Desea descartar los cambios realizados?')) {
      setSettings(originalSettings);
      setMessage(null);
    }
  };

  if (isLoading) {
    return <Loading message="Cargando configuraciones del sistema..." />;
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  return (
    <div className="settings-page">
      {message && (
        <div className={`alert alert--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Información General */}
      <div className="settings-section">
        <div className="section-header">
          <FiGlobe size={24} />
          <div>
            <h3 className="section-title">Información General</h3>
            <p className="section-description">Configuración básica del sistema</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group form-group--full">
            <label className="form-label">Nombre de la Empresa</label>
            <input
              type="text"
              className="form-input"
              value={settings?.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="Betel Natural"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Zona Horaria</label>
            <select
              className="form-input"
              value={settings?.timezone || 'America/Bogota'}
              onChange={(e) => handleChange('timezone', e.target.value)}
            >
              <option value="America/Bogota">America/Bogotá (CO)</option>
              <option value="America/New_York">America/New York (US East)</option>
              <option value="America/Los_Angeles">America/Los Angeles (US West)</option>
              <option value="America/Chicago">America/Chicago (US Central)</option>
              <option value="America/Mexico_City">America/Mexico City (MX)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Idioma por Defecto</label>
            <select
              className="form-input"
              value={settings?.default_language || 'es'}
              onChange={(e) => handleChange('default_language', e.target.value)}
            >
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Moneda por Defecto</label>
            <select
              className="form-input"
              value={settings?.default_currency || 'COP'}
              onChange={(e) => handleChange('default_currency', e.target.value)}
            >
              <option value="COP">COP - Peso Colombiano</option>
              <option value="USD">USD - Dólar Estadounidense</option>
              <option value="EUR">EUR - Euro</option>
              <option value="MXN">MXN - Peso Mexicano</option>
            </select>
          </div>
        </div>
      </div>

      {/* Formatos */}
      <div className="settings-section">
        <div className="section-header">
          <FiClock size={24} />
          <div>
            <h3 className="section-title">Formatos de Fecha y Hora</h3>
            <p className="section-description">Configuración de formatos de visualización</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Formato de Fecha</label>
            <select
              className="form-input"
              value={settings?.date_format || '%d/%m/%Y'}
              onChange={(e) => handleChange('date_format', e.target.value)}
            >
              <option value="%d/%m/%Y">DD/MM/YYYY (31/12/2025)</option>
              <option value="%m/%d/%Y">MM/DD/YYYY (12/31/2025)</option>
              <option value="%Y-%m-%d">YYYY-MM-DD (2025-12-31)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Formato de Hora</label>
            <select
              className="form-input"
              value={settings?.time_format || '%H:%M'}
              onChange={(e) => handleChange('time_format', e.target.value)}
            >
              <option value="%H:%M">24 horas (14:30)</option>
              <option value="%I:%M %p">12 horas (02:30 PM)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Límites y Restricciones */}
      <div className="settings-section">
        <div className="section-header">
          <FiDatabase size={24} />
          <div>
            <h3 className="section-title">Límites y Restricciones</h3>
            <p className="section-description">Configuración de límites del sistema</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="form-label">Tamaño Máx. de Archivo (MB)</label>
            <input
              type="number"
              className="form-input"
              value={settings?.max_file_upload_size_mb || 10}
              onChange={(e) => handleChange('max_file_upload_size_mb', parseInt(e.target.value))}
              min="1"
              max="100"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Timeout de Sesión (minutos)</label>
            <input
              type="number"
              className="form-input"
              value={settings?.session_timeout_minutes || 30}
              onChange={(e) => handleChange('session_timeout_minutes', parseInt(e.target.value))}
              min="5"
              max="1440"
            />
          </div>
        </div>
      </div>

      {/* Características del Sistema */}
      <div className="settings-section">
        <div className="section-header">
          <FiDatabase size={24} />
          <div>
            <h3 className="section-title">Características del Sistema</h3>
            <p className="section-description">Habilitar o deshabilitar funcionalidades</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.enable_notifications || true}
                onChange={(e) => handleChange('enable_notifications', e.target.checked)}
              />
              <span>Habilitar notificaciones</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.enable_email_notifications || true}
                onChange={(e) => handleChange('enable_email_notifications', e.target.checked)}
              />
              <span>Habilitar notificaciones por email</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.enable_audit_log || true}
                onChange={(e) => handleChange('enable_audit_log', e.target.checked)}
              />
              <span>Habilitar registro de auditoría</span>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings?.auto_backup_enabled || false}
                onChange={(e) => handleChange('auto_backup_enabled', e.target.checked)}
              />
              <span>Habilitar respaldos automáticos</span>
            </label>
          </div>

          {settings?.auto_backup_enabled && (
            <div className="form-group">
              <label className="form-label">Frecuencia de Respaldo (días)</label>
              <input
                type="number"
                className="form-input"
                value={settings?.backup_frequency_days || 7}
                onChange={(e) => handleChange('backup_frequency_days', parseInt(e.target.value))}
                min="1"
              />
            </div>
          )}
        </div>
      </div>

      {/* Botones de Acción */}
      <div className="settings-actions">
        <button
          className="btn btn--outline"
          onClick={handleReset}
          disabled={!hasChanges || isSaving}
        >
          <FiRotateCcw size={16} />
          Descartar Cambios
        </button>

        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          <FiSave size={16} />
          {isSaving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

export default SystemSettings;
